import {
  encodeMessage,
  parseServerMessage,
  type PendingAccessRequest,
} from "./accessShareProtocol";

function getAccessShareWsUrl(): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/api/access-share`;
}

export type AccessShareListenerHandle = {
  approve: (args: {
    requestId: string;
    publicKeyA: string;
    encrypted: string;
    iv: string;
  }) => void;
  deny: (args: { requestId: string }) => void;
  stop: () => void;
};

export type AccessShareRequesterHandle = {
  cancel: () => void;
};

export function startListening({
  onRequestsChange,
  onError,
  onDisconnected,
}: {
  onRequestsChange: (args: { requests: PendingAccessRequest[] }) => void;
  onError: (args: { message: string }) => void;
  onDisconnected: () => void;
}): AccessShareListenerHandle {
  let requests: PendingAccessRequest[] = [];
  let closedByUs = false;
  const ws = new WebSocket(getAccessShareWsUrl());

  const emitRequests = () => {
    onRequestsChange({ requests: [...requests] });
  };

  ws.addEventListener("open", () => {
    ws.send(encodeMessage({ payload: { type: "listen" } }));
  });

  ws.addEventListener("message", (event) => {
    const message = parseServerMessage({ raw: String(event.data) });
    if (!message) return;
    if (message.type === "pending") {
      requests = message.requests;
      emitRequests();
      return;
    }
    if (message.type === "request") {
      requests = [
        ...requests.filter((request) => request.id !== message.id),
        {
          id: message.id,
          pairingCode: message.pairingCode,
          publicKey: message.publicKey,
        },
      ];
      emitRequests();
      return;
    }
    if (message.type === "resolved") {
      requests = requests.filter((request) => request.id !== message.requestId);
      emitRequests();
      return;
    }
    if (message.type === "error") {
      onError({ message: message.message });
    }
  });

  ws.addEventListener("close", () => {
    if (!closedByUs) onDisconnected();
  });

  return {
    approve({ requestId, publicKeyA, encrypted, iv }) {
      if (ws.readyState !== WebSocket.OPEN) return;
      ws.send(
        encodeMessage({
          payload: { type: "approve", requestId, publicKeyA, encrypted, iv },
        }),
      );
    },
    deny({ requestId }) {
      if (ws.readyState !== WebSocket.OPEN) return;
      ws.send(encodeMessage({ payload: { type: "deny", requestId } }));
    },
    stop() {
      closedByUs = true;
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(encodeMessage({ payload: { type: "stop" } }));
      }
      ws.close();
    },
  };
}

export function startRequesting({
  publicKey,
  onWaiting,
  onGranted,
  onDenied,
  onError,
  onDisconnected,
}: {
  publicKey: string;
  onWaiting: (args: { pairingCode: string }) => void;
  onGranted: (args: { publicKeyA: string; encrypted: string; iv: string }) => void;
  onDenied: () => void;
  onError: (args: { message: string }) => void;
  onDisconnected: () => void;
}): AccessShareRequesterHandle {
  let settled = false;
  let closedByUs = false;
  const ws = new WebSocket(getAccessShareWsUrl());

  const settle = () => {
    settled = true;
  };

  ws.addEventListener("open", () => {
    ws.send(encodeMessage({ payload: { type: "request", publicKey } }));
  });

  ws.addEventListener("message", (event) => {
    const message = parseServerMessage({ raw: String(event.data) });
    if (!message) return;
    if (message.type === "waiting") {
      onWaiting({ pairingCode: message.pairingCode });
      return;
    }
    if (message.type === "granted") {
      settle();
      onGranted({
        publicKeyA: message.publicKeyA,
        encrypted: message.encrypted,
        iv: message.iv,
      });
      closedByUs = true;
      ws.close();
      return;
    }
    if (message.type === "denied") {
      settle();
      onDenied();
      closedByUs = true;
      ws.close();
      return;
    }
    if (message.type === "error") {
      onError({ message: message.message });
    }
  });

  ws.addEventListener("close", () => {
    if (!closedByUs && !settled) onDisconnected();
  });

  return {
    cancel() {
      closedByUs = true;
      ws.close();
    },
  };
}
